import { useEffect, useRef, useCallback } from 'react'
import { importSelectedCompany } from '../redux/actions/companyList'
import {
  BROADCAST_CHANNEL_NAME,
  getOrCreateTabId,
  writeStoredCompanyIdOnly,
  SELECTED_COMPANY_LS_KEY,
} from '../utils/selectedCompanySync'

/**
 * Listen for company id changes from other tabs (BroadcastChannel + storage).
 * Resolves full company from Redux companyList; queues pending id until list is available.
 */
export function useSelectedCompanySync(dispatch, companyList, selectedCompanyId) {
  const pendingCompanyIdRef = useRef(null)
  const tabIdRef = useRef(null)
  if (tabIdRef.current == null) {
    tabIdRef.current = getOrCreateTabId()
  }

  const tryApplyCompanyId = useCallback(
    (companyId, { writeLs } = { writeLs: true }) => {
      if (companyId == null || companyId === '') return false
      if (String(companyId) === String(selectedCompanyId)) return true

      const list = Array.isArray(companyList) ? companyList : []
      if (list.length === 0) {
        pendingCompanyIdRef.current = String(companyId)
        return false
      }

      const match = list.find((c) => String(c?.id) === String(companyId))
      if (match) {
        if (writeLs) writeStoredCompanyIdOnly(companyId)
        dispatch(importSelectedCompany(match))
        pendingCompanyIdRef.current = null
        return true
      }

      pendingCompanyIdRef.current = String(companyId)
      return false
    },
    [dispatch, companyList, selectedCompanyId]
  )

  useEffect(() => {
    const pending = pendingCompanyIdRef.current
    if (!pending || !Array.isArray(companyList) || companyList.length === 0) return

    const match = companyList.find((c) => String(c?.id) === String(pending))
    if (match) {
      writeStoredCompanyIdOnly(pending)
      dispatch(importSelectedCompany(match))
      pendingCompanyIdRef.current = null
    }
  }, [companyList, dispatch])

  useEffect(() => {
    const localTabId = tabIdRef.current

    const onMessage = (ev) => {
      const d = ev.data
      if (!d || d.v !== 1 || d.companyId == null) return
      if (d.tabId === localTabId) return
      tryApplyCompanyId(d.companyId, { writeLs: true })
    }

    let ch = null
    if (typeof BroadcastChannel !== 'undefined') {
      ch = new BroadcastChannel(BROADCAST_CHANNEL_NAME)
      ch.onmessage = onMessage
    }

    const onStorage = (e) => {
      if (e.key !== SELECTED_COMPANY_LS_KEY || e.newValue == null) return
      if (String(e.newValue) === String(selectedCompanyId)) return
      tryApplyCompanyId(e.newValue, { writeLs: false })
    }

    window.addEventListener('storage', onStorage)

    return () => {
      window.removeEventListener('storage', onStorage)
      if (ch) {
        ch.onmessage = null
        ch.close()
      }
    }
  }, [tryApplyCompanyId, selectedCompanyId])
}
